/**
 * Photo marker creation utilities.
 *
 * Supports two creation modes:
 * - Entity mode: Creates separate OdDb entities (polylines/circles + text) grouped by name
 * - Block mode: Creates an OdDbBlockReference with the marker geometry
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  PhotoMarkerConfig,
  PhotoMarkerMode,
} from "../managers/PhotoMarkerManager";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { createBlock, appendAttributesFromDefinitions } from "../blocks";
import { v4 as uuid } from "uuid";
import type { Textstyle } from "@/types";
import {
  applyTextStyleToEntity,
  DEFAULT_TEXT_STYLE,
} from "../database/textStyle";
import { Logger } from "@/logger";
import { writePhotoMarkerXData } from "./detection";
import { applyScales2D } from "../blocks/transform";
import BlockLibrary from "@/blocks/library";

/** Prefix for photo marker entity names */
export const PHOTO_MARKER_NAME_PREFIX = "photo_marker__";

/** Regex to extract marker number from name */
export const PHOTO_MARKER_NAME_REGEX = new RegExp(
  `${PHOTO_MARKER_NAME_PREFIX}(\\d+)`,
);

/**
 * Result of creating a photo marker.
 */
export interface PhotoMarkerCreationResult {
  /** IDs of all created entities (for undo/redo) */
  entityIds: DrawingModule.OdDbObjectId[];
  /** The marker number that was used */
  markerNumber: number;
  /** The layer name the entities were created on */
  layerName: string;
}

/**
 * Options for creating a photo marker.
 */
export interface CreatePhotoMarkerOptions {
  /** World position to place the marker */
  position: { x: number; y: number; z: number };
  /** The marker configuration (shape) */
  config: PhotoMarkerConfig;
  /** The marker number (internal sequence, used for xData/XRecord — not necessarily displayed) */
  markerNumber: number;
  /** The text actually displayed on the marker (number or formatted date, per legend settings) */
  label: string;
  /** Creation mode: 'entity' or 'block' */
  mode: PhotoMarkerMode;
  /** Layer name to create entities on */
  layerName?: string;
  /** Textstyle use in Attribute text */
  textStyle?: Textstyle;
  /** textSize used for textheight (take priority on textstyle.size if set) */
  scale?: number;
}

/**
 * Generate a unique name for a photo marker.
 */
export function generatePhotoMarkerName(markerNumber: number): string {
  return `${PHOTO_MARKER_NAME_PREFIX}${markerNumber}_${uuid()}`;
}

/**
 * Extract the marker number from a photo marker name.
 */
export function getMarkerNumberFromName(name: string): number | null {
  const match = PHOTO_MARKER_NAME_REGEX.exec(name);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Create a photo marker at the specified position.
 *
 * @param viewer The drawing viewer
 * @param options Creation options
 * @returns The created entity IDs and metadata
 */
export function createPhotoMarker(
  viewer: DRDrawingViewer,
  options: CreatePhotoMarkerOptions,
): PhotoMarkerCreationResult | null {
  const {
    position,
    config,
    markerNumber,
    label,
    mode,
    layerName = "0",
    textStyle,
    scale,
  } = options;

  if (mode === "insert") {
    return createPhotoMarkerAsBlock(
      viewer,
      position,
      config,
      markerNumber,
      label,
      layerName,
      textStyle,
      scale,
    );
  } else {
    return createPhotoMarkerAsEntity(
      viewer,
      position,
      config,
      markerNumber,
      label,
      layerName,
      textStyle,
      scale,
    );
  }
}

/**
 * Create a photo marker as separate entities (polylines/circles + text).
 */
function createPhotoMarkerAsEntity(
  viewer: DRDrawingViewer,
  position: { x: number; y: number; z: number },
  config: PhotoMarkerConfig,
  markerNumber: number,
  label: string,
  layerName: string,
  textStyle?: Textstyle,
  scale = 1,
): PhotoMarkerCreationResult | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    console.error("[PhotoMarker] No database available");
    return null;
  }

  const entityIds: DrawingModule.OdDbObjectId[] = [];

  // Get current space
  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );

  const scaledConfig = BlockLibrary.scaleBlockConfig(config, scale);
  // Create geometry entities based on config
  for (const geom of scaledConfig.geometries) {
    let entityId: DrawingModule.OdDbObjectId | null = null;

    if (geom.type === "circle") {
      entityId = createCircleEntity(
        module,
        db,
        currentSpace,
        position,
        geom as {
          type: "circle";
          center: [number, number, number];
          radius: number;
        },
        layerName,
      );
    } else if (geom.type === "polyline") {
      entityId = createPolylineEntity(
        module,
        db,
        currentSpace,
        position,
        geom as { type: "polyline"; points: Array<[number, number, number]> },
        layerName,
      );
    }

    if (entityId) {
      entityIds.push(entityId);
    }
  }

  // Create text entity showing the marker's label (number or date, per legend settings)
  const textAttr = scaledConfig.attributes?.[0];
  if (textAttr) {
    const textEntityId = createTextEntity(
      module,
      db,
      currentSpace,
      position,
      textAttr.position,
      label,
      layerName,
      textStyle,
      scale,
    );
    if (textEntityId) {
      entityIds.push(textEntityId);
    }
  }

  deleteObj(currentSpace);

  // Signature du marqueur sur l'entité de tête : sans bloc porteur du nom, c'est
  // le seul indice qui distingue ce texte d'un texte ordinaire. L'outil des
  // observations s'en sert pour lister les marqueurs du plan.
  if (entityIds.length > 0) {
    try {
      writePhotoMarkerXData(module, entityIds[0], config.name);
    } catch (e) {
      // Best-effort : un marqueur non tagué reste dessiné, il ne sera juste pas
      // reconnu comme marqueur — jamais de quoi casser le tracé.
      Logger.error(`[PhotoMarker] Could not tag entity-mode marker: ${e}`);
    }
  }

  return {
    entityIds,
    markerNumber,
    layerName,
  };
}

/**
 * Create a photo marker as a block reference with attributes.
 * Uses proper DWG attributes (OdDbAttribute) attached to the block reference.
 */
function createPhotoMarkerAsBlock(
  viewer: DRDrawingViewer,
  position: { x: number; y: number; z: number },
  config: PhotoMarkerConfig,
  markerNumber: number,
  label: string,
  layerName: string,
  textStyle?: Textstyle,
  scale = 1,
): PhotoMarkerCreationResult | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    Logger.error("[PhotoMarker] No database available");
    return null;
  }

  const entityIds: DrawingModule.OdDbObjectId[] = [];

  // Create block definition (forceRecreate to ensure entities have kByBlock color)
  const blockRecordId = createBlock(viewer, config, true);
  if (!blockRecordId || blockRecordId.isNull()) {
    Logger.error("[PhotoMarker] Failed to create block definition");
    return null;
  }

  // Get current space
  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );

  // Create block reference
  const blockRef = module.OdDbBlockReference.createObject();
  blockRef.setDatabaseDefaults(db, true);
  blockRef.setBlockTableRecord(blockRecordId);

  // Set position
  const posPoint = new module.OdGePoint3d(position.x, position.y, position.z);
  blockRef.setPosition(posPoint);
  deleteObj(posPoint);

  // Set layer
  const layerStr = new module.OdString(layerName);
  blockRef.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  // Apply Scale
  applyScales2D(viewer.module, blockRef, scale, scale, 1);

  // compute scaled height to revert scale on text
  // Append to model space first (block ref needs to be in database before adding attributes)
  const blockEntityId = currentSpace.appendOdDbEntity(blockRef);
  entityIds.push(blockEntityId);

  // Instantiate the block record's ATTDEFs on the reference — every attribute of a
  // marker carries its label, plus the legend's text style when one is set.
  const attributeIds = appendAttributesFromDefinitions(
    module,
    blockRef,
    blockRecordId,
    {
      getValue: () => label,
      decorate: textStyle
        ? (attr) =>
            applyTextStyleToEntity(
              module,
              db,
              attr as unknown as DrawingModule.OdDbText,
              textStyle,
            )
        : undefined,
    },
  );
  entityIds.push(...attributeIds);

  deleteObj(blockRef);
  deleteObj(currentSpace);

  return {
    entityIds,
    markerNumber,
    layerName,
  };
}

/**
 * Create a circle entity.
 */
function createCircleEntity(
  module: DRDrawingViewer["module"],
  db: DrawingModule.OdDbDatabase,
  currentSpace: DrawingModule.OdDbBlockTableRecord,
  position: { x: number; y: number; z: number },
  geom: { type: "circle"; center: [number, number, number]; radius: number },
  layerName: string,
): DrawingModule.OdDbObjectId | null {
  const circle = module.OdDbCircle.createObject();
  circle.setDatabaseDefaults(db, true);

  const center = new module.OdGePoint3d(
    position.x + geom.center[0],
    position.y + geom.center[1],
    position.z + geom.center[2],
  );
  circle.setCenter(center);
  circle.setRadius(geom.radius);
  deleteObj(center);

  const layerStr = new module.OdString(layerName);
  circle.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  const entityId = currentSpace.appendOdDbEntity(circle);
  deleteObj(circle);

  return entityId;
}

/**
 * Create a polyline entity.
 */
function createPolylineEntity(
  module: DRDrawingViewer["module"],
  db: DrawingModule.OdDbDatabase,
  currentSpace: DrawingModule.OdDbBlockTableRecord,
  position: { x: number; y: number; z: number },
  geom: { type: "polyline"; points: Array<[number, number, number]> },
  layerName: string,
): DrawingModule.OdDbObjectId | null {
  const polyline = module.OdDbPolyline.createObject();
  polyline.setDatabaseDefaults(db, true);

  for (let i = 0; i < geom.points.length; i++) {
    const [x, y] = geom.points[i];
    const point = new module.OdGePoint2d(position.x + x, position.y + y);
    polyline.addVertexAt(i, point, 0, 0, 0, 0);
    deleteObj(point);
  }

  const layerStr = new module.OdString(layerName);
  polyline.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  const entityId = currentSpace.appendOdDbEntity(polyline);
  deleteObj(polyline);

  return entityId;
}

/**
 * Create a text entity.
 */
function createTextEntity(
  module: DRDrawingViewer["module"],
  db: DrawingModule.OdDbDatabase,
  currentSpace: DrawingModule.OdDbBlockTableRecord,
  position: { x: number; y: number; z: number },
  offset: [number, number, number],
  text: string,
  layerName: string,
  textStyle?: Textstyle,
  textSize?: number,
): DrawingModule.OdDbObjectId | null {
  const textEntity = module.OdDbText.createObject();
  textEntity.setDatabaseDefaults(db, true);

  const textPosition = new module.OdGePoint3d(
    position.x + offset[0],
    position.y + offset[1],
    position.z + offset[2],
  );

  // Apply text style (sets font and base height from style)
  const resolvedStyle = textStyle ?? DEFAULT_TEXT_STYLE;
  applyTextStyleToEntity(module, db, textEntity, resolvedStyle);

  // textSize takes priority over style size
  if (textSize !== undefined) {
    textEntity.setHeight(textSize);
  }

  // Set alignment modes
  // For center alignment, we need to use alignmentPoint, not position
  textEntity.setHorizontalMode(4); // kTextCenter
  textEntity.setVerticalMode(2); // kTextVertMid

  // For non-default alignment, set alignmentPoint (position is ignored)
  textEntity.setAlignmentPoint(textPosition);

  deleteObj(textPosition);

  const textStr = new module.OdString(text);
  textEntity.setTextString(textStr);
  deleteObj(textStr);

  const layerStr = new module.OdString(layerName);
  textEntity.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  const entityId = currentSpace.appendOdDbEntity(textEntity);
  deleteObj(textEntity);

  return entityId;
}
